import { ApolloError, gql, useApolloClient, useMutation, useQuery } from '@apollo/client'
import { message } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import { RcFile } from 'antd/lib/upload'
import dayjs, { Dayjs } from 'dayjs'
import { flatten } from 'lodash'
import { useCallback, useContext, useEffect, useState } from 'react'
import { matchPath, useLocation, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { JOURNAL_ETAT_KANBAN_MUTATION } from '../apollo/mutation/journalEtatKanban.mutation'
import { COPROPRIETE_ALL } from '../apollo/query/copropriete.query'
import { KANBAN_QUERY } from '../apollo/query/kanban.query'
import { PERSONNE_SEARCH_BY_COPRO_QUERY } from '../apollo/query/personne.query'
import { useImperativeQuery } from '../helper'
import { AuthContext } from '../providers/auth.provider'
import { Copropriete, D4_Copropriete, D4_Journaletatkanban, D4_Kanban, D4_Lot } from '../types'

export const useAuth = (): Auth => {
  const auth: Auth = useContext(AuthContext)
  return auth
}

export const useSearchPersonneByCopro = (trg: string) => {
  const searchPersonne = useImperativeQuery<{ lots: D4_Lot[] }, { search: string; id: number; trigramme: string }>(
    PERSONNE_SEARCH_BY_COPRO_QUERY
  )

  const handleSearch = ({ search, id = -1 }: { search: string; id?: number }) => {
    return searchPersonne({ search: `%${search}%`, id, trigramme: trg })
  }

  return handleSearch
}

export const useCustomParams = () => {
  return new URLSearchParams(useLocation().search)
}

export const useSmallScreen = () => {
  const screens = useBreakpoint()

  return screens.xs || (screens.sm && !screens.md)
}

export const useCurrentCopro = (trg?: string): Copropriete | null => {
  const { trigramme } = useParams<{ trigramme: string }>()
  const { data, loading } = useQuery(COPROPRIETE_ALL)
  if (!trigramme && !trg) {
    return null
  }
  if (loading || !data) return null

  return [...data?.managed, ...data?.notManaged].find((c) => c.trigramme === (trg ?? trigramme))
}

/**
 * Comme useCurrentCopro mais en dehors du scope de react-router
 * et uniquement sous la route '/copropriete/*'
 * @returns {params: { trigramme }}
 */
export const useMatchCopro = () => {
  const currentCopro = matchPath<{ trigramme: string }>(window.location.pathname, {
    path: '/copropriete/:trigramme/',
    exact: false,
    strict: false,
  })
  return currentCopro
}

/**
 * read copro
 */

export const useQueryCopro = (
  coproId?: number
): [copro: D4_Copropriete | undefined, getCopro: (coproId: number) => Promise<D4_Copropriete | undefined>] => {
  const [selectedCopro, setSelectedCopro] = useState({} as D4_Copropriete | undefined)
  const { data } = useQuery<{ managed: D4_Copropriete[]; notManaged: D4_Copropriete[] }>(COPROPRIETE_ALL)

  const getCopro = useCallback(
    async (coproId): Promise<D4_Copropriete | undefined> => {
      const selected = [...(data?.managed ?? []), ...(data?.notManaged ?? [])].find((copro) => copro.id === coproId)
      setSelectedCopro(selected)
      return selected
    },
    [data?.managed, data?.notManaged]
  )

  useEffect(() => {
    if (coproId) {
      getCopro(coproId)
    }
  }, [coproId, getCopro])

  return [selectedCopro, getCopro]
}

export const useMessage = ({
  successMessage,
  errorMessage,
  loading,
  error,
  called,
}: HookMessage & {
  loading: boolean
  error: ApolloError | undefined
  called: boolean
}) => {
  useEffect(() => {
    if (called && error) {
      message.error(errorMessage ?? 'Une erreur est survenue')
    } else if (called && !loading && !error && successMessage) {
      message.success(successMessage)
    }
  }, [called, error, errorMessage, loading, successMessage])
}

const SIGNED_URL_QUERY = gql`
  mutation SignedUrl($filename: String!) {
    signed: signedUrlAction(filename: $filename) {
      url
    }
  }
`

export const useAwsUpload = () => {
  const [getSignedUrl] = useMutation<{ signed: { url: string } }, { filename: string }>(SIGNED_URL_QUERY)

  const upload = useCallback(
    async (file: RcFile): Promise<{ res: Response | undefined; filename: string | undefined }> => {
      const filename = uuidv4() + '_' + file.name

      try {
        /**
         * Recupère une url signée par aws s3 pour avoir la permission d'upload sur le bucket
         */
        const apiUrl = await getSignedUrl({
          variables: {
            filename,
          },
        })
        /**
         * PUT le fichier sur AWS S3
         */
        const res = await window.fetch(apiUrl.data?.signed.url ?? '', {
          method: 'PUT',
          body: file,
        })
        return { res, filename }
      } catch (error) {
        message.error("Erreur lors de la recupération de l'url signé")
      }
      return { res: undefined, filename: undefined }
    },
    [getSignedUrl]
  )
  return [upload]
}

const JOURNAL_ETAT_KANBAN_QUERY = gql`
  query JournalEtatKanban($demandeId: bigint!) {
    jek: d4_journaletatkanban(
      where: { _and: [{ demande_id: { _eq: $demandeId } }, { datesortie: { _is_null: true } }] }
    ) {
      id
      numeroentreejournal
    }
  }
`

interface JournalEtatKanbanInput {
  demandeId: number
  datesortie: Dayjs
  dateentree: Dayjs
  dateexpiration: Dayjs | null
  numeroentreejournal: number
  etatkanbanId: number
}

export const useUpdateKanban = (demandeId: number) => {
  const client = useApolloClient()
  const [updateKanbanMutation, { loading }] = useMutation<{ jek: D4_Journaletatkanban }, JournalEtatKanbanInput>(
    JOURNAL_ETAT_KANBAN_MUTATION
  )
  const { data: kanbanData } = useQuery<{ kanban: D4_Kanban[] }>(KANBAN_QUERY)

  const updateKanban = useCallback(
    async (value: number, item: { label: string; value: number; etatkanbanid: number }) => {
      const jek = await client.query<{ jek: D4_Journaletatkanban[] }>({
        query: JOURNAL_ETAT_KANBAN_QUERY,
        fetchPolicy: 'network-only',
        variables: {
          demandeId: demandeId,
        },
      })
      const dateexpiration = flatten(kanbanData?.kanban.map((k) => k.etatkanbans)).find(
        (k) => k.id === item.etatkanbanid
      )?.dureeexpirationpardefaut
      const variables = {
        demandeId: demandeId,
        datesortie: dayjs(), // date de sortie de l'ancien etat
        dateentree: dayjs(),
        dateexpiration: dateexpiration ? dayjs().add(dateexpiration, 'days') : null,
        numeroentreejournal: jek.data.jek[0]?.numeroentreejournal ?? 0 + 1,
        etatkanbanId: item.etatkanbanid,
      }
      updateKanbanMutation({
        variables,
        update: (cache, { data }) => {
          cache.modify({
            id: cache.identify({ __typename: 'd4_fn_view_demande', id: demandeId }),
            fields: {
              couleuretatkanban() {
                return data?.jek.etatkanban?.couleuretatkanban
              },
              kanbanid() {
                return data?.jek.etatkanban?.kanban?.id
              },
              kanbantitre() {
                return data?.jek.etatkanban?.kanban?.titre
              },
              etatkanbanid() {
                return data?.jek.etatkanban?.id
              },
              etatkanbanlibelle() {
                return data?.jek.etatkanban?.libelle
              },
            },
          })
        },
      })
    },
    [client, demandeId, kanbanData?.kanban, updateKanbanMutation]
  )
  return { data: kanbanData?.kanban, loading, updateKanban }
}
